import { ChevronDown } from "lucide-react"
import { type ReactNode, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { useTranslation } from "@/hooks/use-translation"
import type { StoryboardAnimaticRefFrame } from "@/lib/inertia-actions"
import type {
  AnimaticRefMode,
  ShotGenerationRefs,
  ShotGenerationRefsPatch,
  VideoMotionRef,
} from "@/lib/shot-generation-refs"
import { UI_CHOICE_CONTROL_SM } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"

type Option<T extends string> = { id: T; label: string; disabled?: boolean; title?: string }

function Segmented<T extends string>({
  value,
  options,
  disabled,
  onChange,
}: {
  value: T
  options: Option<T>[]
  disabled?: boolean
  onChange: (id: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-0.5 rounded-md bg-ui-surface-sunken p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          title={o.title}
          disabled={disabled || o.disabled}
          aria-pressed={o.id === value}
          onClick={() => onChange(o.id)}
          className={cn(
            "rounded-sm px-2 py-1 text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            o.id === value
              ? "bg-ui-surface-hover text-ui-text-primary"
              : "text-ui-text-secondary enabled:hover:text-ui-text-primary",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Row({ label, hint, children }: { label: string; hint?: string | null; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 px-1 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-ui-text-primary">{label}</span>
        {hint ? <span className="truncate text-[11px] text-ui-text-muted">{hint}</span> : null}
      </div>
      {children}
    </div>
  )
}

type MotionChoice = "off" | "previz" | "depth_gray" | "depth_vis"
type AnimaticChoice = "off" | AnimaticRefMode

function motionChoices(motion: VideoMotionRef): { previz: MotionChoice; animatic: AnimaticChoice } {
  if (motion.kind === "previz") {
    return { previz: motion.depth ? (motion.depth === "vis" ? "depth_vis" : "depth_gray") : "previz", animatic: "off" }
  }
  if (motion.kind === "animatic") return { previz: "off", animatic: motion.mode }
  return { previz: "off", animatic: "off" }
}

/**
 * «Референсы» в пульте: что уйдёт в генерацию сверх героев/локации/своих референсов.
 * Меняет те же флаги, что «Настройки» под промптом; сохраняются вместе с промптом при запуске.
 */
/** Что уходит в генерацию со своих вкладок шота: герои, реквизит, локация, свои @reference_n. */
export type RefContextGroup = {
  key: string
  label: string
  items: { name: string; url: string | null }[]
  onOpen?: () => void
}

/** Сколько миниатюр группы видно в строке; остальное — «+N». */
const CONTEXT_THUMBS = 4

function ContextRow({ group }: { group: RefContextGroup }) {
  const shown = group.items.slice(0, CONTEXT_THUMBS)
  const rest = group.items.length - shown.length
  const names = group.items.map((item) => item.name).join(", ")
  const body = (
    <>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-xs text-ui-text-primary">{group.label}</span>
        <span className="truncate text-[11px] text-ui-text-muted" title={names}>
          {names}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        {shown.map((item, i) =>
          item.url ? (
            <img key={i} src={item.url} alt="" className="h-7 w-7 rounded-sm bg-black object-cover" loading="lazy" />
          ) : (
            <span
              key={i}
              className="flex h-7 w-7 items-center justify-center rounded-sm bg-ui-surface-sunken text-[10px] text-ui-text-muted"
            >
              {item.name.slice(0, 2).toUpperCase()}
            </span>
          ),
        )}
        {rest > 0 ? <span className="px-0.5 text-[11px] text-ui-text-muted tabular-nums">+{rest}</span> : null}
      </span>
    </>
  )
  return group.onOpen ? (
    <button
      type="button"
      onClick={group.onOpen}
      className="flex items-center gap-2 rounded-md px-1 py-1.5 text-left transition-colors hover:bg-ui-surface-hover"
    >
      {body}
    </button>
  ) : (
    <div className="flex items-center gap-2 px-1 py-1.5">{body}</div>
  )
}

export function ShotGenerationRefsPopover({
  kind,
  refs,
  placement,
  onChange,
  context = [],
}: {
  kind: "video" | "frame"
  refs: ShotGenerationRefs
  placement: "above" | "below"
  onChange: (patch: ShotGenerationRefsPatch) => void
  /** Всегда уходящие референсы — показываем, чтобы было видно весь запрос. */
  context?: RefContextGroup[]
}) {
  const { t } = useTranslation(["shot", "common"])
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: placement === "above" ? "up" : "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "22rem" }),
    onOutsideClick: () => setOpen(false),
    onEscape: () => setOpen(false),
  })

  const contextCount = context.reduce((sum, group) => sum + group.items.length, 0)
  const summary: string[] = []
  if (kind === "video") {
    if (refs.video.motion.kind === "previz") {
      summary.push(refs.video.motion.depth ? t("assetShelf.summaryDepth") : t("assetShelf.summaryPreviz"))
    }
    if (refs.video.motion.kind === "animatic") summary.push(t("assetShelf.summaryAnimatic"))
    if (refs.video.motion.kind === "current_video") summary.push(t("assetShelf.summaryCurrentVideo"))
  } else {
    if (refs.frame.sendCurrent) summary.push(t("assetShelf.summaryCurrent"))
    if (refs.frame.animaticFrame) summary.push(t("assetShelf.summaryAnimatic"))
  }

  const choices = motionChoices(refs.video.motion)
  const previzReason = !refs.video.hasPreviz
    ? t("assetShelf.noPreviz")
    : refs.video.previzTooLong
      ? t("assetShelf.previzTooLong")
      : null
  const animaticReason = !refs.video.hasAnimatic || !refs.video.supportsAnimatic ? t("assetShelf.noAnimatic") : null
  const depthTitle = refs.video.previzDepthReady ? undefined : t("assetShelf.depthNotReady")

  const setPreviz = (choice: MotionChoice) => {
    if (choice === "off") {
      onChange({ motion: choices.animatic === "off" ? { kind: "off" } : { kind: "animatic", mode: choices.animatic } })
      return
    }
    const depth = choice === "depth_gray" ? "gray" : choice === "depth_vis" ? "vis" : null
    onChange({ motion: { kind: "previz", depth } })
  }
  const setAnimatic = (choice: AnimaticChoice) => {
    onChange({ motion: choice === "off" ? { kind: "off" } : { kind: "animatic", mode: choice } })
  }

  const menu =
    open && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className="flex max-h-[70vh] flex-col overflow-y-auto rounded-lg border border-ui-border-strong p-2 shadow-xl shadow-black/50 backdrop-blur-md dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
      >
        <p className="px-1 pt-0.5 pb-1 text-[13px] font-medium text-ui-text-primary">
          {kind === "video" ? t("assetShelf.refsTitleVideo") : t("assetShelf.refsTitleFrame")}
        </p>
        {context.length > 0 ? (
          <>
            {context.map((group) => (
              <ContextRow key={group.key} group={group} />
            ))}
            <div className="mx-1 my-1 h-px bg-ui-border" />
          </>
        ) : null}
        {kind === "video" ? (
          <>
            <label
              className={cn(
                "flex items-start gap-2 rounded-md px-1 py-1.5",
                refs.video.hasCurrentVideo ? "cursor-pointer" : "cursor-not-allowed opacity-50",
              )}
            >
              <input
                type="checkbox"
                className={cn(UI_CHOICE_CONTROL_SM, "mt-0.5")}
                checked={refs.video.motion.kind === "current_video"}
                disabled={!refs.video.hasCurrentVideo}
                onChange={(e) => onChange({ motion: e.target.checked ? { kind: "current_video" } : { kind: "off" } })}
              />
              <span className="flex min-w-0 flex-col">
                <span className="text-xs text-ui-text-primary">{t("assetShelf.currentVideo")}</span>
                <span className="text-[11px] text-ui-text-muted">
                  {refs.video.hasCurrentVideo ? t("assetShelf.currentVideoHint") : t("assetShelf.noCurrentVideo")}
                </span>
              </span>
            </label>
            <Row label={t("assetShelf.previz")} hint={previzReason}>
              <Segmented<MotionChoice>
                value={choices.previz}
                disabled={Boolean(previzReason)}
                onChange={setPreviz}
                options={[
                  { id: "off", label: t("assetShelf.none") },
                  { id: "previz", label: t("assetShelf.original") },
                  {
                    id: "depth_gray",
                    label: t("assetShelf.depthGray"),
                    disabled: !refs.video.previzDepthReady,
                    title: depthTitle,
                  },
                  {
                    id: "depth_vis",
                    label: t("assetShelf.depthColor"),
                    disabled: !refs.video.previzDepthReady,
                    title: depthTitle,
                  },
                ]}
              />
            </Row>
            <Row label={t("assetShelf.animatic")} hint={animaticReason}>
              <Segmented<AnimaticChoice>
                value={choices.animatic}
                disabled={Boolean(animaticReason)}
                onChange={setAnimatic}
                options={[
                  { id: "off", label: t("assetShelf.none") },
                  { id: "video_silent", label: t("assetShelf.animSilent") },
                  { id: "video_with_audio", label: t("assetShelf.animWithAudio") },
                  { id: "audio_only", label: t("assetShelf.animAudioOnly") },
                ]}
              />
            </Row>
            <p className="px-1 pt-1 text-[11px] leading-snug text-ui-text-muted">
              {t("assetShelf.motionExclusiveHintWithVideo")} {t("assetShelf.savedOnGenerate")}
            </p>
          </>
        ) : (
          <>
            <label
              className={cn(
                "flex items-start gap-2 rounded-md px-1 py-1.5",
                refs.frame.hasCurrent ? "cursor-pointer" : "cursor-not-allowed opacity-50",
              )}
            >
              <input
                type="checkbox"
                className={cn(UI_CHOICE_CONTROL_SM, "mt-0.5")}
                checked={refs.frame.sendCurrent}
                disabled={!refs.frame.hasCurrent}
                onChange={(e) => onChange({ sendCurrent: e.target.checked })}
              />
              <span className="flex min-w-0 flex-col">
                <span className="text-xs text-ui-text-primary">{t("assetShelf.currentFrame")}</span>
                <span className="text-[11px] text-ui-text-muted">{t("assetShelf.currentFrameHint")}</span>
              </span>
            </label>
            <Row
              label={t("assetShelf.animaticFrame")}
              hint={refs.frame.hasAnimatic ? t("assetShelf.animaticFrameHint") : t("assetShelf.noAnimatic")}
            >
              <Segmented<"off" | StoryboardAnimaticRefFrame>
                value={refs.frame.animaticFrame ?? "off"}
                disabled={!refs.frame.hasAnimatic}
                onChange={(id) => onChange({ animaticFrame: id === "off" ? null : id })}
                options={[
                  { id: "off", label: t("assetShelf.none") },
                  { id: "first", label: t("common.first") },
                  { id: "poster", label: t("common.poster") },
                  { id: "last", label: t("common.last") },
                ]}
              />
            </Row>
            <p className="px-1 pt-1 text-[11px] leading-snug text-ui-text-muted">{t("assetShelf.savedOnGenerate")}</p>
          </>
        )}
      </div>
    ) : null

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs whitespace-nowrap text-ui-text-secondary transition-colors hover:bg-ui-surface-hover hover:text-ui-text-primary",
          open && "bg-ui-surface-hover text-ui-text-primary",
        )}
      >
        {contextCount > 0 ? t("assetShelf.referencesCount", { count: contextCount }) : t("assetShelf.references")}
        {summary.length > 0 ? <span className="text-ui-accent-text">· {summary.join(", ")}</span> : null}
        <ChevronDown className="h-3 w-3 text-ui-text-muted" />
      </button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}

/**
 * Пульт вкладок «Аниматик» и «Превиз»: генерации своей нет, пульт решает, в каком виде
 * этот ассет уйдёт в генерацию видео. Те же флаги, что в «Референсах» видео.
 */
export function ShotMotionSendPanel({
  source,
  refs,
  onChange,
  action,
}: {
  source: "animatic" | "previz"
  refs: ShotGenerationRefs
  onChange: (patch: ShotGenerationRefsPatch) => void
  /** Генерация этого ассета в той же плашке (у превиза — маска глубины). */
  action?: ReactNode
}) {
  const { t } = useTranslation(["shot", "common"])
  const choices = motionChoices(refs.video.motion)
  const reason =
    source === "previz"
      ? !refs.video.hasPreviz
        ? t("assetShelf.noPreviz")
        : refs.video.previzTooLong
          ? t("assetShelf.previzTooLong")
          : null
      : !refs.video.hasAnimatic || !refs.video.supportsAnimatic
        ? t("assetShelf.noAnimatic")
        : null
  const depthTitle = refs.video.previzDepthReady ? undefined : t("assetShelf.depthNotReady")

  return (
    <div className="flex min-h-12 w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg bg-ui-surface-raised px-3 py-2 light:bg-ui-surface-sunken">
      <span className="text-xs text-ui-text-secondary">{t("assetShelf.sendToVideo")}</span>
      {source === "previz" ? (
        <Segmented<MotionChoice>
          value={choices.previz}
          disabled={Boolean(reason)}
          onChange={(choice) => {
            if (choice === "off") {
              onChange({
                motion: choices.animatic === "off" ? { kind: "off" } : { kind: "animatic", mode: choices.animatic },
              })
              return
            }
            const depth = choice === "depth_gray" ? "gray" : choice === "depth_vis" ? "vis" : null
            onChange({ motion: { kind: "previz", depth } })
          }}
          options={[
            { id: "off", label: t("assetShelf.none") },
            { id: "previz", label: t("assetShelf.original") },
            {
              id: "depth_gray",
              label: t("assetShelf.depthGray"),
              disabled: !refs.video.previzDepthReady,
              title: depthTitle,
            },
            {
              id: "depth_vis",
              label: t("assetShelf.depthColor"),
              disabled: !refs.video.previzDepthReady,
              title: depthTitle,
            },
          ]}
        />
      ) : (
        <Segmented<AnimaticChoice>
          value={choices.animatic}
          disabled={Boolean(reason)}
          onChange={(choice) =>
            onChange({ motion: choice === "off" ? { kind: "off" } : { kind: "animatic", mode: choice } })
          }
          options={[
            { id: "off", label: t("assetShelf.none") },
            { id: "video_silent", label: t("assetShelf.animSilent") },
            { id: "video_with_audio", label: t("assetShelf.animWithAudio") },
            { id: "audio_only", label: t("assetShelf.animAudioOnly") },
          ]}
        />
      )}
      {action ? (
        <div className="ml-auto flex shrink-0 items-center">{action}</div>
      ) : (
        <span className="ml-auto truncate text-[11px] text-ui-text-muted">
          {reason ?? t("assetShelf.savedOnGenerate")}
        </span>
      )}
    </div>
  )
}
